import { useLayoutEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { applyScale, calculateScaleFromSize } from "@design-lab/entities/artwork/lib/artworkScale";
import { getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import type { Surface } from "@design-lab/features/canvas/model/types";
import type { ArtworkDimensions } from "@design-lab/entities/artwork/types";

type AutoSizingParams = {
  form: UseFormReturn<any>;
  placement: PlacementFormPosition;
  surface: Surface | undefined;
  artworkImage: HTMLImageElement | undefined;
  artworkSizeBounds: ArtworkDimensions | undefined;
};

/**
 * Hook to automatically size and scale artwork based on placement and image changes
 * Extracted from original Artboard component
 */
export function useDesignLabArtworkAutoSizing({
  form,
  placement,
  surface,
  artworkImage,
  artworkSizeBounds
}: AutoSizingParams) {
  const prevValuesRef = useRef({
    surface,
    artworkImage,
    placement
  });

  useLayoutEffect(() => {
    if (!artworkSizeBounds || artworkSizeBounds.maxWidthInches === 0) {
      return;
    }

    const prev = prevValuesRef.current;
    const artworkImageChanged = prev.artworkImage !== artworkImage;
    const placementChanged = prev.placement !== placement;

    prevValuesRef.current = {
      surface,
      artworkImage,
      placement
    };

    const currentSize = form.getValues(`${placement}.size`);
    const infoPath = getSideFromPlacement(placement);
    const currentScale = form.getValues(`info.${infoPath}.scale`);
    const { minWidthInches, minHeightInches, maxWidthInches, maxHeightInches } = artworkSizeBounds;

    if (!currentSize || (currentSize as any)?.width === 0 || (currentSize as any)?.height === 0) {
      const newSize = {
        width: maxWidthInches,
        height: maxHeightInches
      };

      form.setValue(`${placement}.size`, newSize);
      form.setValue(`info.${infoPath}.scale`, 1.0);
      return;
    }

    if (currentScale && (artworkImageChanged || placementChanged)) {
      const newSize = applyScale(currentScale, artworkSizeBounds);

      if (newSize.width !== currentSize.width || newSize.height !== currentSize.height) {
        form.setValue(`${placement}.size`, newSize);
      }
      return;
    }

    const { width, height } = currentSize as any;
    const currentAspectRatio = Number((width / height).toFixed(2));

    let finalSize: { width: number; height: number };

    if (currentAspectRatio !== artworkSizeBounds.maxAspectRatioByWidth) {
      if (artworkSizeBounds.maxAspectRatioByWidth > 1) {
        // Image is wide: keep current width, calculate height based on aspect ratio
        const newHeight = width / artworkSizeBounds.maxAspectRatioByWidth;
        finalSize = {
          width,
          height: Math.max(minHeightInches, Math.min(newHeight, maxHeightInches))
        };
      } else {
        // Image is tall: keep current height, calculate width based on aspect ratio
        const newWidth = height * artworkSizeBounds.maxAspectRatioByWidth;
        finalSize = {
          width: Math.max(minWidthInches, Math.min(newWidth, maxWidthInches)),
          height
        };
      }
    } else {
      finalSize = {
        width: Math.max(minWidthInches, Math.min(width, maxWidthInches)),
        height: Math.max(minHeightInches, Math.min(height, maxHeightInches))
      };
    }

    if (finalSize.width !== width || finalSize.height !== height) {
      form.setValue(`${placement}.size`, finalSize);
    }

    const scale = calculateScaleFromSize(finalSize, artworkSizeBounds);
    form.setValue(`info.${infoPath}.scale`, scale);
  }, [artworkImage, artworkSizeBounds, placement, surface, form]);
}
